import { useState, useEffect, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { uid } from '../../shared/utils/helpers';
import RotatePlayLogo from '../components/RotatePlayLogo';
import type { Player, SkillLevel, Session, League } from '../../shared/types';
import { KioskRestService } from '../services/kioskRestService';

const API_URL = import.meta.env.VITE_API_URL as string;

interface KioskProps {
  mode: 'register' | 'scores';
}

export default function Kiosk({ mode }: KioskProps) {
  const { leagueId }    = useParams<{ leagueId: string }>();
  const [searchParams]  = useSearchParams();
  const token           = searchParams.get('token') ?? '';

  const [league,    setLeague]    = useState<League | null>(null);
  const [players,   setPlayers]   = useState<Player[]>([]);
  const [session,   setSession]   = useState<Session | null>(null);
  const [loading,   setLoading]   = useState(true);
  const [authError, setAuthError] = useState(false);

  const svcRef = useRef<KioskRestService | null>(null);

  useEffect(() => {
    if (!leagueId || !token) {
      setAuthError(true);
      setLoading(false);
      return;
    }

    const svc = new KioskRestService({
      apiUrl:        API_URL,
      leagueId,
      delegateToken: token,
      onAuthError:   () => { setAuthError(true); setLoading(false); },
      onFirstLoad:   () => setLoading(false),
    });
    svcRef.current = svc;

    const unsubs = [
      svc.onLeague(setLeague),
      svc.onPlayers(setPlayers),
      svc.onSession(setSession),
    ];

    return () => {
      unsubs.forEach(fn => fn());
      svc.destroy();
      svcRef.current = null;
    };
  }, [leagueId, token]);

  return (
    <div style={{ minHeight: '100vh', background: 'var(--green)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div style={{ background: '#fff', borderRadius: 20, boxShadow: '0 12px 40px rgba(0,0,0,.2)', padding: '36px 30px', maxWidth: 440, width: '100%' }}>
        <div style={{ textAlign: 'center', marginBottom: 10 }}>
          <RotatePlayLogo size={60} animated />
        </div>

        {authError ? (
          <KioskAuthError />
        ) : loading ? (
          <div style={{ textAlign: 'center', padding: 40 }}>
            <p style={{ color: 'var(--gray-500)' }}>Connecting…</p>
          </div>
        ) : mode === 'register' ? (
          <KioskRegister
            league={league}
            players={players}
            onAddPlayer={p => svcRef.current!.addPlayer(p)}
          />
        ) : (
          <KioskScores
            league={league}
            players={players}
            session={session}
            onSetSession={s => svcRef.current!.setSession(s)}
          />
        )}
      </div>
    </div>
  );
}

function KioskAuthError() {
  return (
    <div style={{ textAlign: 'center' }}>
      <div style={{ fontSize: '2.5rem', marginBottom: 10 }}>🔒</div>
      <h2 style={{ fontWeight: 800, color: 'var(--red)', marginBottom: 8 }}>Invalid Kiosk Link</h2>
      <p style={{ color: 'var(--gray-500)', fontSize: '.875rem' }}>
        This link has expired or is invalid. Ask your league organizer for a fresh kiosk URL.
      </p>
    </div>
  );
}

// ──────────────────────────────────────────────────────────
//  Registration sub-view
// ──────────────────────────────────────────────────────────

interface RegisterProps {
  league:      League | null;
  players:     Player[];
  onAddPlayer: (p: Player) => Promise<void>;
}

function KioskRegister({ league, players, onAddPlayer }: RegisterProps) {
  const [name,   setName]   = useState('');
  const [email,  setEmail]  = useState('');
  const [dupr,   setDupr]   = useState('');
  const [skill,  setSkill]  = useState<SkillLevel | ''>('');
  const [error,  setError]  = useState('');
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState<{ msg: string; emailSent: boolean } | null>(null);

  const spotsLeft = league ? Math.max(0, league.maxPlayers - players.length) : null;
  const isFull    = spotsLeft === 0;

  async function register() {
    const trimmed      = name.trim();
    const emailTrimmed = email.trim().toLowerCase();
    setError('');

    if (!trimmed) { setError('Please enter your name.'); return; }
    if (players.some(p => p.name.toLowerCase() === trimmed.toLowerCase())) {
      setError(`"${trimmed}" is already registered.`); return;
    }
    if (isFull) { setError('Registration is full. See the organizer.'); return; }
    if (emailTrimmed && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailTrimmed)) {
      setError('Please enter a valid email address, or leave it blank.'); return;
    }

    const duprVal = parseFloat(dupr);
    const player: Player = {
      id:     uid(),
      name:   trimmed,
      dupr:   isNaN(duprVal) ? null : duprVal,
      skill:  skill || null,
      joined: new Date().toISOString(),
      ...(emailTrimmed ? { email: emailTrimmed } : {}),
    };

    setSaving(true);
    try {
      await onAddPlayer(player);
      setSuccess({
        msg:       `You're player #${players.length + 1}${league ? ' in ' + league.name : ''}.`,
        emailSent: !!emailTrimmed,
      });
    } catch {
      setError('Failed to save. Check your connection and try again.');
      setSaving(false);
    }
  }

  if (success) {
    return (
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: '3.5rem', marginBottom: 10 }}>🎉</div>
        <h2 style={{ fontSize: '1.3rem', fontWeight: 800, color: 'var(--green-d)', marginBottom: 6 }}>
          You're registered!
        </h2>
        <p style={{ color: 'var(--gray-500)', fontSize: '.9rem', marginBottom: 6 }}>{success.msg}</p>
        {success.emailSent && (
          <p style={{ color: 'var(--gray-400)', fontSize: '.82rem', marginBottom: 20 }}>
            📧 A confirmation has been sent to your email.
          </p>
        )}
        <button
          className="btn btn-secondary btn-full"
          onClick={() => { setSuccess(null); setName(''); setEmail(''); setDupr(''); setSkill(''); }}
        >
          Register Another Player
        </button>
      </div>
    );
  }

  return (
    <>
      <h1 style={{ fontSize: '1.4rem', fontWeight: 800, color: 'var(--green-d)', textAlign: 'center', marginBottom: 4 }}>
        {league?.name ?? 'RotatePlay'}
      </h1>
      <p style={{ fontSize: '.88rem', color: 'var(--gray-500)', textAlign: 'center', marginBottom: 20 }}>
        {spotsLeft != null && spotsLeft > 0
          ? `${spotsLeft} spot${spotsLeft === 1 ? '' : 's'} remaining — add yourself!`
          : isFull ? 'Registration is full.' : 'Register to join the league.'
        }
      </p>

      {error && <div className="alert alert-warn" style={{ marginBottom: 14 }}>{error}</div>}

      {isFull ? (
        <div className="alert alert-red">Max players reached. See the organizer.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            <label>Your Name *</label>
            <input
              value={name}
              onChange={e => setName(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && register()}
              placeholder="First and last name"
              style={{ marginTop: 4, fontSize: '1.1rem' }}
            />
          </div>
          <div>
            <label>
              Email{' '}
              <span style={{ fontWeight: 400, color: 'var(--gray-400)', fontSize: '.82rem' }}>
                (optional — for confirmation)
              </span>
            </label>
            <input
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="you@example.com"
              style={{ marginTop: 4 }}
            />
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 4 }}>
              <label>DUPR Rating</label>
              <input
                type="number" step="0.01" min="2" max="6"
                value={dupr}
                onChange={e => setDupr(e.target.value)}
                placeholder="e.g. 3.75"
              />
            </div>
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 4 }}>
              <label>Skill Level</label>
              <select value={skill} onChange={e => setSkill(e.target.value as SkillLevel | '')}>
                <option value="">Select…</option>
                {(['Beginner', 'Intermediate', 'Advanced', 'Elite'] as SkillLevel[]).map(s => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
          </div>
          <button
            className="btn btn-primary btn-full btn-lg"
            onClick={register}
            disabled={saving}
            style={{ marginTop: 4 }}
          >
            {saving ? 'Saving…' : 'Join League →'}
          </button>
        </div>
      )}
    </>
  );
}

// ──────────────────────────────────────────────────────────
//  Score entry sub-view
// ──────────────────────────────────────────────────────────

interface ScoresProps {
  league:       League | null;
  players:      Player[];
  session:      Session | null;
  onSetSession: (s: Session) => Promise<void>;
}

function KioskScores({ league, players, session, onSetSession }: ScoresProps) {
  const [scoreEdits, setScoreEdits] = useState<Record<string, string>>({});
  const [submitted,  setSubmitted]  = useState(false);

  const playerName = (id: string) => players.find(p => p.id === id)?.name ?? id;

  // Reset score inputs and submitted flag whenever the organizer starts a new round
  useEffect(() => {
    setScoreEdits({});
    setSubmitted(false);
  }, [session?.round]);

  if (!session || !session.courts.length) {
    return (
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: '2.5rem', marginBottom: 10 }}>⏳</div>
        <h2 style={{ fontWeight: 800, color: 'var(--green-d)', marginBottom: 6 }}>No Active Session</h2>
        <p style={{ color: 'var(--gray-500)', fontSize: '.88rem' }}>
          Ask the organizer to start a session.
        </p>
      </div>
    );
  }

  // Only show the success screen if the server confirms all courts are submitted.
  // This prevents a stale submitted=true from persisting after the organizer advances the round.
  const allCourtsActuallySubmitted = session.courts.every(c => c.submitted);
  if (submitted && allCourtsActuallySubmitted) {
    return (
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: '3.5rem', marginBottom: 10 }}>✅</div>
        <h2 style={{ fontWeight: 800, color: 'var(--green-d)', marginBottom: 6 }}>Scores Submitted!</h2>
        <p style={{ color: 'var(--gray-500)', fontSize: '.88rem', marginBottom: 20 }}>
          The organizer will advance to the next round.
        </p>
        <button className="btn btn-secondary btn-full" onClick={() => setSubmitted(false)}>
          View Courts
        </button>
      </div>
    );
  }

  async function submitCourt(i: number) {
    if (!session) return;
    const sA = scoreEdits[`A-${i}`] ?? '';
    const sB = scoreEdits[`B-${i}`] ?? '';
    if (sA === '' || sB === '') { alert('Please enter both scores.'); return; }
    const updatedCourts = session.courts.map((c, idx) =>
      idx === i ? { ...c, scoreA: parseInt(sA), scoreB: parseInt(sB), submitted: true } : c
    );
    await onSetSession({ ...session, courts: updatedCourts });
    if (updatedCourts.every(c => c.submitted)) setSubmitted(true);
  }

  async function undoCourt(i: number) {
    if (!session) return;
    const updatedCourts = session.courts.map((c, idx) =>
      idx === i ? { ...c, scoreA: null, scoreB: null, submitted: false } : c
    );
    await onSetSession({ ...session, courts: updatedCourts });
  }

  return (
    <>
      <h1 style={{ fontSize: '1.4rem', fontWeight: 800, color: 'var(--green-d)', textAlign: 'center', marginBottom: 4 }}>
        Enter Scores
      </h1>
      <p style={{ fontSize: '.88rem', color: 'var(--gray-500)', textAlign: 'center', marginBottom: 20 }}>
        {league?.name ?? 'RotatePlay'} · Round {session.round}
      </p>

      {session.courts.map((court, i) => {
        if (court.submitted) {
          return (
            <div key={i} style={{
              border: '2px solid var(--green)', borderRadius: 12,
              padding: 14, marginBottom: 10, background: 'var(--green-l)',
            }}>
              <div style={{ fontWeight: 800, color: 'var(--green-d)', marginBottom: 8 }}>
                🏟 Court {i + 1} <span style={{ fontWeight: 700, fontSize: '.8rem' }}>✓ Submitted</span>
              </div>
              <div style={{ fontSize: '.85rem', fontWeight: 600, color: 'var(--gray-700)', marginBottom: 3 }}>
                Team A: {court.teamA.map(playerName).join(' & ')}
              </div>
              <div style={{ fontSize: '.85rem', fontWeight: 600, color: 'var(--gray-700)' }}>
                Team B: {court.teamB.map(playerName).join(' & ')}
              </div>
              <div style={{ textAlign: 'center', margin: '8px 0', fontWeight: 800, fontSize: '1.2rem', color: 'var(--green-d)' }}>
                {court.scoreA} – {court.scoreB}
              </div>
              <button className="btn btn-secondary btn-sm btn-full" style={{ marginTop: 4 }} onClick={() => undoCourt(i)}>
                ↩ Undo Submission
              </button>
            </div>
          );
        }

        return (
          <div key={i} style={{ border: '2px solid var(--green)', borderRadius: 12, padding: 14, marginBottom: 10 }}>
            <div style={{ fontWeight: 800, color: 'var(--green-d)', marginBottom: 8 }}>🏟 Court {i + 1}</div>
            <div style={{ fontSize: '.85rem', fontWeight: 600, color: 'var(--gray-700)', marginBottom: 3 }}>
              Team A: {court.teamA.map(playerName).join(' & ')}
            </div>
            <div style={{ fontSize: '.85rem', fontWeight: 600, color: 'var(--gray-700)', marginBottom: 10 }}>
              Team B: {court.teamB.map(playerName).join(' & ')}
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <span style={{ fontSize: '.85rem', fontWeight: 600 }}>A:</span>
              <input
                type="number" min={0} max={99}
                value={scoreEdits[`A-${i}`] ?? ''}
                onChange={e => setScoreEdits(p => ({ ...p, [`A-${i}`]: e.target.value }))}
                placeholder="0"
                style={{ flex: 1, padding: 8, textAlign: 'center', fontSize: '1.1rem', fontWeight: 800, border: '2px solid var(--gray-300)', borderRadius: 8, outline: 'none' }}
              />
              <span style={{ fontWeight: 800, color: 'var(--gray-500)' }}>–</span>
              <input
                type="number" min={0} max={99}
                value={scoreEdits[`B-${i}`] ?? ''}
                onChange={e => setScoreEdits(p => ({ ...p, [`B-${i}`]: e.target.value }))}
                placeholder="0"
                style={{ flex: 1, padding: 8, textAlign: 'center', fontSize: '1.1rem', fontWeight: 800, border: '2px solid var(--gray-300)', borderRadius: 8, outline: 'none' }}
              />
              <span style={{ fontSize: '.85rem', fontWeight: 600 }}>B</span>
            </div>
            <button
              className="btn btn-primary btn-full"
              style={{ marginTop: 10 }}
              onClick={() => submitCourt(i)}
            >
              Submit Court {i + 1} ✔
            </button>
          </div>
        );
      })}
    </>
  );
}
